import {
  signalStore,
  withState,
  withComputed,
  withMethods,
  patchState
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { computed, inject } from '@angular/core';
import { catchError, of, pipe, switchMap, tap } from 'rxjs';
import { initialSettingsState } from './settings.model';
import { SettingsDataService } from '../../services/settings-data.service';
import { errorMessage } from '../../shared/util/helpers/error.helper';
import { LegalityDeclarationMethodEnum } from '../../enums/legality-declaration-method.enum';

export const SettingsStore = signalStore(
  { providedIn: 'root' },
  withState(initialSettingsState),

  withComputed((store) => ({
    riskSettingsMap: computed(() => {
      const risks = store.settings()?.risks || [];
      return new Map(risks.map((risk) => [risk?.name, risk?.display]));
    }),

    allowMitigationUploadSetting: computed(
      () => store.settings()?.allowMitigationAssetUpload ?? false
    ),

    allowProceedSurveyRisksSetting: computed(
      () => store.settings()?.settings?.allow_proceed_survey_risks ?? true
    ),

    BLDDSUploadRequired: computed(
      () => store.settings()?.settings?.BLDDSUploadRequired ?? false
    ),

    filenamePrefillTrn: computed(
      () => store.settings()?.settings?.filenamePrefillTrn ?? false
    ),

    legalityDeclarationMethod: computed(
      () =>
        store.settings()?.settings?.legalityDeclarationMethod ??
        LegalityDeclarationMethodEnum.FILE_UPLOAD
    ),

    DDRTemplate: computed(
      () => store.settings()?.settings?.DDRTemplate ?? null
    ),

    plotManagement: computed(() => store.settings()?.plotManagement ?? null),

    risks: computed(() => store.settings()?.risks ?? null),

    bufferConfig: computed(
      () => store.settings()?.settings?.buffer_config ?? null
    ),

    mapView: computed(
      () => store.settings()?.settings?.map_view?.enabled ?? null
    ),

    blockOverlapsSetting: computed(
      () => store.settings()?.settings?.blockOverlaps ?? false
    )
  })),

  withMethods((store, settingsDataService = inject(SettingsDataService)) => ({
    loadSettings: rxMethod<{ portal: string; supplierId: number | null }>(
      pipe(
        tap(() => patchState(store, { isLoading: true, error: null })),
        switchMap(({ portal, supplierId }) =>
          settingsDataService
            .loadSupplierPortalSettings(portal, supplierId)
            .pipe(
              tap((settings) =>
                patchState(store, {
                  settings,
                  isLoading: false
                })
              ),
              catchError((error: unknown) => {
                patchState(store, {
                  error: errorMessage(error) || 'Failed to load settings',
                  isLoading: false
                });
                return of(null);
              })
            )
        )
      )
    ),

    clearSettings: () => {
      patchState(store, initialSettingsState);
    }
  }))
);
